import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Printer, Trash2 } from "lucide-react";
import { billingApi, billingQueryKeys } from "../../shared/api/billing";
import {
  clinicFeaturesApi,
  clinicFeaturesQueryKeys,
} from "../../shared/api/clinicFeatures";
import { LineAllocationEditor } from "./components/LineAllocationEditor";
import { einvoiceApi, einvoiceQueryKeys } from "../../shared/api/einvoice";
import { depositApi, depositQueryKeys } from "../../shared/api/deposit";
import { apiError } from "../../shared/api/client";
import {
  formatDateTime,
  formatPhone,
  formatVndWithUnit,
  invoiceStatusLabel,
  paymentMethodLabel,
} from "../../shared/format";
import { PageHeader } from "../../shared/ui/PageHeader";
import { PatientLink } from "../../shared/ui/PatientLink";
import { ErrorState } from "../../shared/ui/ErrorState";
import { Skeleton } from "../../shared/ui/Skeleton";
import { InvoiceStatusBadge } from "./components/InvoiceStatusBadge";
import { InvoiceCouponCard } from "./components/InvoiceCouponCard";
import { RecordPaymentModal } from "./components/RecordPaymentModal";
import { CancelInvoiceButton } from "./components/CancelInvoiceButton";
import type { AllocationInputRow, VatCategory } from "../../shared/types/billing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const VAT_LABEL: Record<VatCategory, string> = {
  KCT: "KCT",
  VAT_0: "0%",
  VAT_5: "5%",
  VAT_8: "8%",
  VAT_10: "10%",
};

export function InvoiceDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [bhytEditing, setBhytEditing] = useState(false);
  const [bhytInput, setBhytInput] = useState("");
  /** Which line's allocation editor is open, if any (#18837). */
  const [allocatingItemId, setAllocatingItemId] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: billingQueryKeys.invoiceDetail(id),
    queryFn: () => billingApi.get(id),
    enabled: Boolean(id),
  });

  // Only clinics that actually use BHYT see any of this (ADR-0027).
  const clinicFeatures = useQuery({
    queryKey: clinicFeaturesQueryKeys.current(),
    queryFn: () => clinicFeaturesApi.get(),
    staleTime: 5 * 60_000,
  });
  const bhytEnabled = clinicFeatures.data?.bhytEnabled ?? false;

  const saveAllocations = useMutation({
    mutationFn: (v: { itemId: string; rows: AllocationInputRow[] }) =>
      billingApi.replaceAllocations(id, v.itemId, v.rows),
    onSuccess: () => {
      setAllocatingItemId(null);
      qc.invalidateQueries({ queryKey: billingQueryKeys.invoiceDetail(id) });
      qc.invalidateQueries({ queryKey: ["billing", "invoices"] });
    },
  });

  const post = useMutation({
    mutationFn: () => billingApi.post(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingQueryKeys.invoiceDetail(id) });
      qc.invalidateQueries({ queryKey: ["billing", "invoices"] });
    },
  });

  const setBhyt = useMutation({
    mutationFn: (coveredVnd: number) =>
      billingApi.setBhyt(id, { paymentType: "BHYT", coveredVnd }),
    onSuccess: () => {
      setBhytEditing(false);
      qc.invalidateQueries({ queryKey: billingQueryKeys.invoiceDetail(id) });
      qc.invalidateQueries({ queryKey: ["billing", "invoices"] });
    },
  });

  const voidIt = useMutation({
    mutationFn: (reason: string) => billingApi.void(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingQueryKeys.invoiceDetail(id) });
      qc.invalidateQueries({ queryKey: ["billing", "invoices"] });
    },
  });

  // E-invoice (hóa đơn điện tử) — status shown once the invoice is fully paid; issue button lets
  // staff push it to PA Vietnam on demand (auto-issue tenants have it done in the background).
  const einvoicePaid = detail.data?.status === "PAID";
  const einvoice = useQuery({
    queryKey: einvoiceQueryKeys.status(id),
    queryFn: () => einvoiceApi.status(id),
    enabled: Boolean(id) && einvoicePaid,
  });
  const issueEinvoice = useMutation({
    mutationFn: () => einvoiceApi.issue(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: einvoiceQueryKeys.status(id) }),
  });

  // Tạm ứng — apply the patient's deposit to this invoice (records a DEPOSIT payment + debits the
  // deposit balance atomically on the backend). Only offered while the invoice still has a balance.
  const invPatientId = detail.data?.patientId;
  const canCollectDeposit =
    detail.data?.status === "POSTED" || detail.data?.status === "PARTIALLY_PAID";
  const depositBalance = useQuery({
    queryKey: invPatientId ? depositQueryKeys.balance(invPatientId) : ["deposit-balance", "none"],
    queryFn: () => depositApi.balance(invPatientId as string),
    enabled: Boolean(invPatientId) && canCollectDeposit,
  });
  const applyDeposit = useMutation({
    mutationFn: () => {
      const bal = depositBalance.data ?? 0;
      const due = detail.data?.balanceDue ?? 0;
      return depositApi.apply(invPatientId as string, id, Math.min(Number(bal), Number(due)));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingQueryKeys.invoiceDetail(id) });
      qc.invalidateQueries({ queryKey: ["billing", "invoices"] });
      if (invPatientId)
        qc.invalidateQueries({ queryKey: depositQueryKeys.balance(invPatientId) });
    },
  });

  if (detail.isLoading) {
    return (
      <div>
        <PageHeader title="Hoá đơn" />
        <Skeleton height={400} />
      </div>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <div>
        <PageHeader title="Hoá đơn" />
        <ErrorState
          error={detail.error ?? new Error("Không tìm thấy hoá đơn.")}
          title="Không tải được hoá đơn"
          onRetry={() => detail.refetch()}
        />
      </div>
    );
  }

  const inv = detail.data;
  const canPost = inv.status === "DRAFT";
  /**
   * Any line allocated → `invoices.bhyt_covered_vnd` is DERIVED from the lines (#18833) and the
   * server refuses to have it typed. Mirrored here so the UI never offers that edit.
   */
  const hasAllocations = inv.items.some((it) => it.allocations.length > 0);
  // Mirrors what the server allows (#18830): allocations are editable only while the invoice is
  // open and untouched by money. Offering the control otherwise would invite a guaranteed 409.
  const editable =
    (inv.status === "DRAFT" || inv.status === "POSTED") && inv.paidTotal === 0;
  const canCollect = inv.status === "POSTED" || inv.status === "PARTIALLY_PAID";
  const canVoid = inv.status === "DRAFT" || inv.status === "POSTED" || inv.status === "PARTIALLY_PAID";
  const canPrint = inv.status !== "DRAFT" && inv.status !== "VOIDED";

  return (
    <div>
      <PageHeader
        title={inv.code}
        description={
          <span className="inline-flex items-center gap-3">
            <InvoiceStatusBadge status={inv.status} />
            {inv.paymentType === "BHYT" && <Badge variant="secondary">BHYT</Badge>}
            <span>
              Tạo lúc {formatDateTime(inv.createdAt)}
              {inv.createdBy ? ` bởi ${inv.createdBy}` : ""}
            </span>
          </span>
        }
        breadcrumbs={[
          { label: "Hoá đơn", to: "/invoices" },
          { label: inv.code },
        ]}
        actions={
          <>
            <Button asChild variant="ghost">
              <Link to="/invoices">
                <ArrowLeft />
                Quay lại
              </Link>
            </Button>
            {canPost && (
              <Button
                type="button"
                onClick={() => post.mutate()}
                disabled={post.isPending}
              >
                Ghi nhận hoá đơn
              </Button>
            )}
            {canCollect && (
              <Button type="button" onClick={() => setPaymentOpen(true)}>
                Thu tiền
              </Button>
            )}
            {/* Tự ẩn khi server nói không huỷ được — quy tắc nằm ở DB, không ở đây (#18998). */}
            <CancelInvoiceButton invoiceId={id} />
            {canCollect && (depositBalance.data ?? 0) > 0 && (
              <Button
                type="button"
                variant="outline"
                disabled={applyDeposit.isPending}
                onClick={() => applyDeposit.mutate()}
                title={`Số dư tạm ứng: ${formatVndWithUnit(depositBalance.data ?? 0)}`}
              >
                {applyDeposit.isPending ? "Đang trừ…" : "Trừ tạm ứng"}
              </Button>
            )}
            {canPrint && (
              <Button asChild variant="secondary">
                <Link to={`/invoices/${inv.id}/receipt`}>
                  <Printer />
                  In biên lai
                </Link>
              </Button>
            )}
            {canVoid && (
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  const reason = window.prompt("Lý do hủy hoá đơn:");
                  if (reason && reason.trim()) voidIt.mutate(reason.trim());
                }}
                disabled={voidIt.isPending}
              >
                <Trash2 />
                Hủy hoá đơn
              </Button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardContent className="space-y-1.5 p-5">
            <h3 className="text-sm font-semibold text-muted-foreground">Bệnh nhân</h3>
            <PatientLink
              id={inv.patientId}
              name={inv.patientName}
              className="text-lg font-semibold"
            />
            <div className="font-mono text-sm text-muted-foreground">
              {inv.patientPhone ? formatPhone(inv.patientPhone) : "Chưa có SĐT"}
            </div>
            {inv.doctorName && (
              <div className="text-sm">
                <span className="text-muted-foreground">Bác sĩ:</span>{" "}
                <strong>{inv.doctorName}</strong>
              </div>
            )}
            <div className="text-sm">
              <span className="text-muted-foreground">Phát hành:</span>{" "}
              <strong>{formatDateTime(inv.issuedAt)}</strong>
            </div>
            {inv.postedAt && (
              <div className="text-sm">
                <span className="text-muted-foreground">Ghi nhận:</span>{" "}
                <strong>{formatDateTime(inv.postedAt)}</strong>
              </div>
            )}
            {inv.voidedAt && (
              <div className="text-sm text-destructive">
                <span>Hủy:</span> <strong>{formatDateTime(inv.voidedAt)}</strong>
                {inv.voidReason && <div>— {inv.voidReason}</div>}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
              Mục thanh toán
            </h3>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">#</TableHead>
                    <TableHead>Tên</TableHead>
                    <TableHead className="w-[70px]">ĐVT</TableHead>
                    <TableHead className="w-[60px] text-right">SL</TableHead>
                    <TableHead className="w-[130px] text-right">Đơn giá</TableHead>
                    <TableHead className="w-[130px] text-right">Giảm</TableHead>
                    <TableHead className="w-20 text-right">VAT</TableHead>
                    <TableHead className="w-[140px] text-right">Thành tiền</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inv.items.map((it, idx) => (
                    <TableRow key={it.id}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{it.name}</TableCell>
                      <TableCell className="font-mono">{it.unit ?? "—"}</TableCell>
                      <TableCell className="text-right">{it.quantity}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {formatVndWithUnit(it.unitPrice)}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {it.discountType && it.discountValue != null
                          ? `${it.discountValue}${it.discountType === "PERCENT" ? "%" : "đ"}`
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {it.vatCategory ? VAT_LABEL[it.vatCategory] : "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        <strong>{formatVndWithUnit(it.lineTotal)}</strong>
                        {bhytEnabled && (
                          <div className="mt-1 text-xs font-normal">
                            {it.allocations.length > 0 ? (
                              <span className="text-muted-foreground">
                                BHYT{" "}
                                {formatVndWithUnit(
                                  it.allocations
                                    .filter((a) => a.payer === "BHYT")
                                    .reduce((s, a) => s + a.amountVnd, 0)
                                )}{" "}
                                · BN{" "}
                                {formatVndWithUnit(
                                  it.allocations
                                    .filter((a) => a.payer === "PATIENT")
                                    .reduce((s, a) => s + a.amountVnd, 0)
                                )}
                              </span>
                            ) : (
                              <span className="text-amber-600">Chưa phân bổ</span>
                            )}
                            {editable && (
                              <button
                                type="button"
                                className="ml-2 text-primary hover:underline"
                                onClick={() =>
                                  setAllocatingItemId(
                                    allocatingItemId === it.id ? null : it.id
                                  )
                                }
                              >
                                {allocatingItemId === it.id ? "Đóng" : "Phân bổ"}
                              </button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {bhytEnabled && allocatingItemId && (
              <div className="mt-4">
                {(() => {
                  const it = inv.items.find((x) => x.id === allocatingItemId);
                  if (!it) return null;
                  return (
                    <LineAllocationEditor
                      key={it.id}
                      item={it}
                      disabled={!editable}
                      saving={saveAllocations.isPending}
                      onCancel={() => setAllocatingItemId(null)}
                      onSave={(rows) =>
                        saveAllocations.mutate({ itemId: it.id, rows })
                      }
                    />
                  );
                })()}
                {saveAllocations.isError && (
                  <p className="mt-2 text-sm text-destructive" role="alert">
                    Không lưu được phân bổ: {apiError(saveAllocations.error).detail}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-semibold text-muted-foreground">Tổng kết</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span>Tạm tính</span>
                <span className="font-mono tabular-nums">
                  {formatVndWithUnit(inv.subtotal)}
                </span>
              </div>
              {inv.discountTotal > 0 && (
                <div className="flex items-center justify-between">
                  <span>Giảm giá</span>
                  <span className="font-mono tabular-nums text-amber-600">
                    −{formatVndWithUnit(inv.discountTotal)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span>VAT</span>
                <span className="font-mono tabular-nums">
                  {formatVndWithUnit(inv.vatTotal)}
                </span>
              </div>
              {inv.paymentType === "BHYT" && (
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5">
                    BHYT chi trả
                    {/*
                      Once any line is allocated, this number is DERIVED from the lines (#18833)
                      and the server refuses to have it typed. Offering "Sửa" here would be
                      offering an action that cannot succeed, so the label says where it comes
                      from instead.
                    */}
                    {hasAllocations && (
                      <span className="text-xs text-muted-foreground">(theo phân bổ dòng)</span>
                    )}
                    {!hasAllocations &&
                      (inv.status === "DRAFT" || inv.status === "POSTED") &&
                      inv.paidTotal === 0 &&
                      !bhytEditing && (
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline"
                          onClick={() => {
                            setBhytInput(String(inv.bhytCoveredVnd ?? 0));
                            setBhytEditing(true);
                          }}
                        >
                          Sửa
                        </button>
                      )}
                  </span>
                  {bhytEditing && !hasAllocations ? (
                    <span className="flex items-center gap-1">
                      <Input
                        type="number"
                        min={0}
                        value={bhytInput}
                        onChange={(e) => setBhytInput(e.target.value)}
                        className="h-8 w-28"
                      />
                      <Button
                        type="button"
                        size="sm"
                        disabled={setBhyt.isPending}
                        onClick={() => setBhyt.mutate(Number(bhytInput) || 0)}
                      >
                        Lưu
                      </Button>
                    </span>
                  ) : (
                    <span className="font-mono tabular-nums text-sky-600">
                      −{formatVndWithUnit(inv.bhytCoveredVnd ?? 0)}
                    </span>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between border-t pt-2 text-base font-semibold">
                <span>
                  {inv.paymentType === "BHYT" ? "Bệnh nhân phải trả" : "Tổng cộng"}
                </span>
                <span className="tabular-nums">{formatVndWithUnit(inv.grandTotal)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Đã thu</span>
                <span className="font-mono tabular-nums text-emerald-600">
                  {formatVndWithUnit(inv.paidTotal)}
                </span>
              </div>
              <div className="flex items-center justify-between border-t pt-2 text-base font-semibold text-primary">
                <span>Còn phải thu</span>
                <span className="tabular-nums">{formatVndWithUnit(inv.balanceDue)}</span>
              </div>
            </div>
            {/* Sits under the totals because that is what it changes (#18723). Renders nothing on
                Lite, or once the invoice is paid/void — the backend refuses either way. */}
            <InvoiceCouponCard
              invoiceId={inv.id}
              invoiceStatus={inv.status}
              paidTotal={inv.paidTotal}
              onChanged={() => {
                void qc.invalidateQueries({ queryKey: billingQueryKeys.invoiceDetail(id) });
              }}
            />
            {inv.paymentType === "BHYT" && (
              <p className="mt-3 text-xs text-muted-foreground">
                BHYT chi trả là <strong>gợi ý theo mức hưởng trên thẻ</strong> — vui lòng kiểm tra &amp;
                điều chỉnh; số giám định cuối do cổng BHYT quyết định.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
              Lịch sử thu tiền
            </h3>
            {inv.payments.length === 0 ? (
              <p className="text-sm text-muted-foreground">Chưa có thanh toán nào.</p>
            ) : (
              <ul className="space-y-3">
                {inv.payments.map((p) => (
                  <li key={p.id} className="border-b pb-3 last:border-b-0 last:pb-0">
                    <div className="text-xs text-muted-foreground">
                      {formatDateTime(p.receivedAt)}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <strong>{formatVndWithUnit(p.amount)}</strong>
                      <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        {paymentMethodLabel[p.method]}
                      </span>
                      {p.reference && (
                        <span className="font-mono text-sm text-muted-foreground">
                          · {p.reference}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {p.cashierName}
                      {p.note && ` — ${p.note}`}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {einvoicePaid && (
          <Card className="lg:col-span-3">
            <CardContent className="p-5">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold text-muted-foreground">
                  Hóa đơn điện tử
                </h3>
                {einvoice.data && einvoice.data.status !== "NOT_ISSUED" && (
                  <Badge
                    variant={
                      einvoice.data.status === "ISSUED"
                        ? "success"
                        : einvoice.data.status === "FAILED"
                          ? "destructive"
                          : "warning"
                    }
                  >
                    {einvoice.data.status}
                  </Badge>
                )}
              </div>
              {einvoice.data?.status === "ISSUED" ? (
                <div className="grid gap-1 text-sm">
                  <div>
                    Mã tra cứu: <strong>{einvoice.data.lookupCode ?? "—"}</strong>
                  </div>
                  <div className="text-muted-foreground">
                    Ký hiệu {einvoice.data.serial ?? "—"} ·{" "}
                    {einvoice.data.sentCqt ? "đã gửi Cơ quan Thuế" : "chưa gửi CQT"}
                  </div>
                </div>
              ) : (
                <div className="grid gap-2">
                  <p className="m-0 text-sm text-muted-foreground">
                    {einvoice.data?.status === "FAILED"
                      ? `Phát hành thất bại: ${einvoice.data.lastError ?? "lỗi không rõ"}`
                      : "Chưa phát hành hóa đơn điện tử cho hóa đơn này."}
                  </p>
                  <div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={issueEinvoice.isPending}
                      onClick={() => issueEinvoice.mutate()}
                    >
                      {issueEinvoice.isPending
                        ? "Đang phát hành…"
                        : "Phát hành hóa đơn điện tử"}
                    </Button>
                    {issueEinvoice.isError && (
                      <p className="mb-0 mt-2 text-sm text-destructive">
                        {apiError(issueEinvoice.error).status === 402
                          ? "Tính năng hóa đơn điện tử thuộc gói Pro."
                          : apiError(issueEinvoice.error).detail}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="lg:col-span-3">
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
              Lịch sử thay đổi
            </h3>
            <ul className="space-y-3">
              {inv.events.map((e, i) => (
                <li key={i} className="border-b pb-3 last:border-b-0 last:pb-0">
                  <div className="text-xs text-muted-foreground">
                    {formatDateTime(e.occurredAt)}
                  </div>
                  <div className="text-sm">
                    <strong>{e.actor}</strong> · {e.action}
                    {e.fromStatus && e.toStatus && (
                      <span>
                        {" "}
                        · {invoiceStatusLabel[e.fromStatus]} → {invoiceStatusLabel[e.toStatus]}
                      </span>
                    )}
                    {e.note && (
                      <div className="text-muted-foreground">{e.note}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {paymentOpen && (
        <RecordPaymentModal invoice={inv} onClose={() => setPaymentOpen(false)} />
      )}
    </div>
  );
}
